import moment from "moment";
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Dropdown, Form, Row, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useRequest } from "@components/AppHook";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { Awesome, DevModal } from "@components/Helpers";
import { buildQS, downloadBase64File, getAttachement, parseQS, prevMonthPeriod, url_reverse } from "@components/Utils";

// Пауза между опросами статуса отчёта и её потолок, до которого пауза растёт при ошибках MoneyWatch.
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_INTERVAL_MS = 60000;

const PROJECTS = [
    { value: "store", label: "Store" },
    { value: "market", label: "Market" },
    { value: "cloud", label: "Cloud" },
];

function UploadFilesBlock({ uploadFileTypes, paymentPeriod }) {
    const [uploadProject, setUploadProject] = useState("store");
    const [uploadFileType, setUploadFileType] = useState(uploadFileTypes[0].value);
    const [uploadFile, setUploadFile] = useState(null);
    // Реестр выплат не загружается в MoneyWatch, а собирается из файла — у него своя форма.
    const isRegistry = uploadFileType === "payment_registry";

    return (
        <Card className="mt-3">
            <Card.Header>Загрузка файлов</Card.Header>
            <Card.Body>
                <Table>
                    <thead>
                        <tr>
                            {!isRegistry && <th width={"10%"}>Проект</th>}
                            <th width={"30%"}>Тип</th>
                            {!isRegistry && <th>Файл</th>}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {!isRegistry && (
                                <td>
                                    <Dropdown onSelect={(value) => setUploadProject(value)} size="sm">
                                        <Dropdown.Toggle variant="outline-primary" size="sm" style={{ width: "100%" }}>
                                            {PROJECTS.find((p) => p.value === uploadProject)?.label}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu style={{ width: "100%" }}>
                                            {PROJECTS.map((p) => (
                                                <Dropdown.Item
                                                    key={p.value}
                                                    eventKey={p.value}
                                                    active={uploadProject === p.value}
                                                >
                                                    {p.label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </td>
                            )}
                            <td>
                                <Dropdown onSelect={(value) => setUploadFileType(value)} size="sm">
                                    <Dropdown.Toggle variant="outline-primary" size="sm" style={{ width: "100%" }}>
                                        {uploadFileTypes.find((f) => f.value === uploadFileType)?.label}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu style={{ width: "100%" }}>
                                        {uploadFileTypes.map((f) => (
                                            <Dropdown.Item
                                                key={f.value}
                                                eventKey={f.value}
                                                active={uploadFileType === f.value}
                                            >
                                                {f.label}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </td>
                            {!isRegistry && (
                                <td>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Form.Control
                                            type="file"
                                            size="sm"
                                            accept=".csv,.xlsx,.xls"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    // Сохраняем выбранный файл в состояние
                                                    setUploadFile(file);
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            disabled={!uploadFile}
                                            onClick={async () => {
                                                if (!uploadFile) return;

                                                const formData = new FormData();
                                                formData.append("file", uploadFile);
                                                formData.append("project", uploadProject);
                                                formData.append("file_type", uploadFileType);

                                                try {
                                                    const response = await fetch(
                                                        url_reverse("api:finstatements:upload"),
                                                        {
                                                            method: "POST",
                                                            body: formData,
                                                        }
                                                    );

                                                    const result = await response.json();

                                                    if (result.status === "ok") {
                                                        setUploadFile(null);
                                                    } else {
                                                        // resp_fail кладёт текст ошибки в errmsg
                                                        alert(`Ошибка: ${result.errmsg || "Неизвестная ошибка"}`);
                                                    }
                                                } catch (error) {
                                                    console.error("Error uploading file:", error);
                                                    alert("Ошибка при загрузке файла");
                                                }
                                            }}
                                        >
                                            Отправить
                                        </Button>
                                    </div>
                                </td>
                            )}
                        </tr>
                    </tbody>
                </Table>
                {isRegistry && <PaymentRegistryForm paymentPeriod={paymentPeriod} />}
            </Card.Body>
        </Card>
    );
}

function formatBytesToReadableSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const sizes = ["Байт", "Кб", "Мб", "Гб", "Тб"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / 1024 ** i).toFixed(2)} ${sizes[i]}`;
}

function formatTimestampToReadableDate(isoTimestamp) {
    if (!isoTimestamp) return "Н/Д";
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const date = new Date(isoTimestamp + "Z"); // Добавляем 'Z' для указания UTC времени

    const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone, // Указание пользовательской тайм-зоны
    };
    return new Intl.DateTimeFormat("ru-RU", options).format(date);
}

const fileDownloadLink = (filePath) => {
    const base64FilePath = btoa(filePath);
    return url_reverse("api:finstatements:report:download", {}, { filepath: base64FilePath });
};

const strPeriodToDate = (period) => {
    const year = parseInt(period.slice(0, 4), 10);
    const month = parseInt(period.slice(4, 6), 10) - 1;
    return new Date(year, month, 1);
};

// Тот же текст MoneyWatch подставит сам, если поле назначения очистить.
const defaultRegistryPurpose = (paymentPeriod) => {
    if (!paymentPeriod) return "";
    const period = strPeriodToDate(paymentPeriod);
    const month = period.toLocaleString("ru-RU", { month: "long" });
    return `Оплата по соглашению с разработчиком за ${month} ${period.getFullYear()}`;
};

const formatRub = (amount) =>
    parseFloat(amount).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PaymentRegistrySummary({ result }) {
    const totals = [
        ["Во входном файле", result.source],
        ["В реестре", result.registry],
        ["Проблемные", result.problem],
    ];
    const shift = parseFloat(result.rounding_shift);

    return (
        <div className="mt-3">
            <p>
                <b>Скачан «{result.file_name}»</b>
            </p>
            <Table size="sm">
                <tbody>
                    {totals.map(([label, total]) => (
                        <tr key={label}>
                            <td>{label}</td>
                            <td>строк: {total.rows}</td>
                            <td>{formatRub(total.amount)} ₽</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {shift !== 0 && (
                <p>
                    <i>
                        Округление до копеек сдвинуло итог на {shift > 0 ? "+" : ""}
                        {formatRub(result.rounding_shift)} ₽
                    </i>
                </p>
            )}
            {result.problems.length > 0 && (
                <Table size="sm" striped>
                    <thead>
                        <tr>
                            <th>MDM ID</th>
                            <th>Наименование</th>
                            <th>Сумма</th>
                            <th>Причина</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* MDM ID во входном файле может повторяться — ключ с индексом */}
                        {result.problems.map((problem, index) => (
                            <tr key={`${index}-${problem.mdm_id}`}>
                                <td>{problem.mdm_id}</td>
                                <td>{problem.name}</td>
                                <td>{formatRub(problem.amount)} ₽</td>
                                <td>{problem.reason}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}

function PaymentRegistryForm({ paymentPeriod }) {
    const [file, setFile] = useState(null);
    const [desiredDate, setDesiredDate] = useState(moment().format("YYYY-MM-DD"));
    const [deadlineDate, setDeadlineDate] = useState(moment().add(1, "day").format("YYYY-MM-DD"));
    const [purpose, setPurpose] = useState(defaultRegistryPurpose(paymentPeriod));
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    // Сменили период страницы — назначение собирается заново, ручная правка теряется.
    useEffect(() => {
        setPurpose(defaultRegistryPurpose(paymentPeriod));
    }, [paymentPeriod]);

    const submit = async () => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("payment_period", paymentPeriod);
        formData.append("desired_date", desiredDate);
        formData.append("deadline_date", deadlineDate);
        formData.append("purpose", purpose);

        setIsLoading(true);
        setError(null);
        setResult(null);
        try {
            const response = await fetch(url_reverse("api:finstatements:payment_registry"), {
                method: "POST",
                body: formData,
            });
            const data = await response.json();
            if (data.status === "ok") {
                downloadBase64File(data.file_content, data.file_name);
                setResult(data);
            } else {
                setError(data.errmsg || "Неизвестная ошибка");
            }
        } catch (e) {
            console.error("Error building payment registry:", e);
            setError("Ошибка при формировании реестра");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Row className="g-2 align-items-end">
                <Col md={4}>
                    <Form.Label>Файл</Form.Label>
                    <Form.Control
                        type="file"
                        size="sm"
                        accept=".xlsx"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                </Col>
                <Col md={2}>
                    <Form.Label>Желаемая дата</Form.Label>
                    <DevForm.DatePicker value={desiredDate} onChange={setDesiredDate} wrapperClassName="w-100" />
                </Col>
                <Col md={2}>
                    <Form.Label>Крайняя дата</Form.Label>
                    <DevForm.DatePicker value={deadlineDate} onChange={setDeadlineDate} wrapperClassName="w-100" />
                </Col>
                <Col md={4}>
                    <Form.Label>Назначение платежа</Form.Label>
                    <Form.Control size="sm" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
                </Col>
            </Row>
            <div className="d-flex justify-content-end mt-2">
                <Button variant="primary" size="sm" disabled={!file || isLoading} onClick={submit}>
                    {isLoading ? <Spinner as="span" animation="border" size="sm" /> : "Сформировать"}
                </Button>
            </div>
            {error && (
                <Alert variant="danger" className="mt-3">
                    {error}
                </Alert>
            )}
            {result && <PaymentRegistrySummary result={result} />}
        </>
    );
}

const ReportInfoModal = ({ show, setShow }) => {
    // модальное окно при открытии которого загружается информация об отчете
    const [reportInfo, setReportInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const reportPeriod = new URLSearchParams(location.search).get("payment_period");
    const reportDeveloperId = new URLSearchParams(location.search).get("developer_id");
    const projectId = new URLSearchParams(location.search).get("project");

    // нужно сделать ожидание, чтобы пользователь не видел пустое окно

    const reportFiles = () => {
        const reportInfoFiles = reportInfo.files;
        const filesArray = [];
        if (!reportInfoFiles || !reportInfoFiles.length) {
            return filesArray;
        }

        for (let i = 0; i < reportInfoFiles.length; i++) {
            const reportFile = reportInfoFiles[i];
            let fileName = reportFile.file_type;
            if (reportFile.file_type === "report") {
                fileName = "Отчет";
            } else if (reportFile.file_type === "act") {
                fileName = "Акт";
            } else if (reportFile.file_type === "act_code") {
                fileName = "Акт по кодам";
            } else if (reportFile.file_type === "act_download") {
                fileName = "Акт по играм";
            } else if (reportFile.file_type === "report_all") {
                fileName = "Полный отчет по транзакциям";
            } else if (reportFile.file_type === "incorrect_transactions") {
                fileName = "Некорректные транзакции";
            } else if (reportFile.file_type === "incorrect_devs") {
                fileName = "Некорректные разработчики";
            } else if (reportFile.file_type === "raw_dmr_data") {
                fileName = "Сырые данные отчета (csv)";
            } else if (reportFile.file_type === "dr_arub_report") {
                fileName = "Реестр операций цифрового рубля";
            }
            filesArray.push(
                <tr key={reportFile.file_url}>
                    <td>{fileName}</td>
                    <td>
                        <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => {
                                window.open(fileDownloadLink(reportFile.file_url), "_blank");
                            }}
                        >
                            Скачать
                        </Button>
                    </td>
                    <td>{formatBytesToReadableSize(reportFile.file_size)}</td>
                    <td>{formatTimestampToReadableDate(reportFile.created_at)}</td>
                    <td>{formatTimestampToReadableDate(reportFile.updated_at)}</td>
                </tr>
            );
        }
        return filesArray;
    };

    useEffect(() => {
        if (show && reportPeriod) {
            setIsLoading(true);
            const fetchReportInfo = async () => {
                try {
                    const response = await fetch(
                        url_reverse(
                            "api:finstatements:report:info",
                            {},
                            {
                                payment_period: reportPeriod,
                                developer_id: reportDeveloperId,
                                project: projectId,
                            }
                        )
                    );
                    if (!response.ok) {
                        throw new Error(`Ошибка загрузки: ${response.statusText}`);
                    }
                    const data = await response.json();
                    setReportInfo(data);
                } catch (error) {
                    console.error("Ошибка загрузки информации об отчете:", error);
                } finally {
                    setTimeout(() => setIsLoading(false), 500);
                    setIsLoading(false);
                }
            };

            fetchReportInfo();
        } else {
            setReportInfo(null);
        }
    }, [show, reportPeriod, reportDeveloperId]);

    if (!show) return null;
    const paymentPeriodStr = strPeriodToDate(reportPeriod);

    return (
        <DevModal
            size="lg"
            show={show}
            onHide={() => setShow(false)}
            header={
                <>
                    Отчет за период{" "}
                    <u>
                        {paymentPeriodStr.toLocaleString("ru-RU", { month: "long" })} {paymentPeriodStr.getFullYear()}
                    </u>
                </>
            }
            body={
                isLoading ? (
                    <>Загрузка...</>
                ) : reportInfo ? (
                    <>
                        <h5>Суммы</h5>
                        <Table className="m-2">
                            <tbody>
                                <tr>
                                    <td>
                                        <b>Дата и время генерации</b>
                                    </td>
                                    <td>{formatTimestampToReadableDate(reportInfo.updated_at)}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <b>Выручка</b>
                                    </td>
                                    <td>{parseFloat(reportInfo.total_income)?.toLocaleString("ru-RU")} ₽</td>
                                </tr>
                                <tr>
                                    <td>
                                        <b>Рефанды</b>
                                    </td>
                                    <td>{parseFloat(reportInfo.total_refund)?.toLocaleString("ru-RU")} ₽</td>
                                </tr>
                                <tr>
                                    <td>
                                        <b>Общая сумма</b>
                                    </td>
                                    <td>{parseFloat(reportInfo.sum_amount)?.toLocaleString("ru-RU")} ₽</td>
                                </tr>

                                <tr>
                                    <td>
                                        <b>Сумма вознаграждения</b>
                                    </td>
                                    <td>{parseFloat(reportInfo.dev_reward)?.toLocaleString("ru-RU")} ₽</td>
                                </tr>
                                <tr>
                                    <td>
                                        <b>Агентская комиссия</b>
                                    </td>
                                    <td>{parseFloat(reportInfo.agent_fee)?.toLocaleString("ru-RU")} ₽</td>
                                </tr>
                                <tr>
                                    <td>
                                        <b>Роялти</b>
                                    </td>
                                    <td>{parseFloat(reportInfo.royalty)?.toLocaleString("ru-RU")} ₽</td>
                                </tr>
                            </tbody>
                        </Table>
                        <h5>Файлы</h5>
                        <Table className="m-2">
                            <thead>
                                <tr>
                                    <th>Тип</th>
                                    <th>Ссылка</th>
                                    <th>Размер</th>
                                    <th>Создан</th>
                                    <th>Обновлен</th>
                                </tr>
                            </thead>
                            <tbody>{reportFiles(reportInfo.files)}</tbody>
                        </Table>
                    </>
                ) : (
                    <>Нет данных</>
                )
            }
        />
    );
};

const ReportDeleteConfirmModal = ({ show, setShow, onConfirm }) => {
    const [isProcessing, setIsProcessing] = useState(false);

    const handleDelete = async () => {
        setIsProcessing(true);
        try {
            await onConfirm();
            setShow(false);
        } catch (error) {
            console.error("delete report error:", error);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <DevModal
            show={show}
            onHide={() => setShow(false)}
            header="Удалить данные отчета"
            body={
                <>
                    <p className="text-danger">
                        <strong>Будут удалены все данные отчета. Это необратимое действие!</strong>
                    </p>
                    <p>Вы уверены, что хотите продолжить?</p>
                </>
            }
            footer={
                <>
                    <Button variant="secondary" onClick={() => setShow(false)} disabled={isProcessing}>
                        Отмена
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={isProcessing}>
                        {isProcessing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Удаление...
                            </>
                        ) : (
                            "Удалить"
                        )}
                    </Button>
                </>
            }
        />
    );
};

const ReportUnlockConfirmModal = ({ show, setShow, onConfirm }) => {
    const [isProcessing, setIsProcessing] = useState(false);

    const handleConfirm = async () => {
        setIsProcessing(true);
        try {
            await onConfirm();
        } catch (error) {
            console.error("unlock report error:", error);
        }
    };

    const unlockButton = (
        <Button variant="primary" onClick={handleConfirm}>
            Разблокировать
        </Button>
    );

    const spinnerButton = (
        <Button variant="primary" disabled>
            <Spinner animation="border" size="sm" />
        </Button>
    );

    return (
        <DevModal
            show={show}
            onHide={() => setShow(false)}
            header="Разблокировка отчета"
            body={<p>Этот отчет финализирован, Вы действительно хотите его разблокировать для внесения правок?</p>}
            footer={
                <>
                    <Button variant="secondary" onClick={() => setShow(false)}>
                        Отмена
                    </Button>
                    {isProcessing ? spinnerButton : unlockButton}
                </>
            }
        />
    );
};

const ButtonCol = ({ name, variant, disabled, awesomeIcon, label, onClick }) => {
    return (
        <Col
            display-lg={"block"}
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={3}
            className="d-flex justify-content-center mb-md-0 mt-xs-1 mt-sm-1"
        >
            <Button
                name={name}
                width="100%"
                variant={variant}
                onClick={onClick}
                style={{ width: "100%" }}
                disabled={disabled}
            >
                <span style={{ pointerEvents: "none" }}>
                    <Awesome type={awesomeIcon} /> {label}
                </span>
            </Button>
        </Col>
    );
};

function SyncedAt({ last_ts }) {
    if (!last_ts) return "Н/Д";
    // Если строка не содержит смещения или 'Z', добавляем 'Z' для интерпретации как UTC
    let tsString = last_ts;
    if (!/Z|[+-]\d{2}:?\d{2}$/.test(tsString)) {
        tsString += "Z";
    }
    const tsDate = new Date(tsString);
    if (isNaN(tsDate)) return "Н/Д";
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone,
    };
    return new Intl.DateTimeFormat("ru-RU", options).format(tsDate);
}

// Единственный источник статуса отчёта: unary-ручка MoneyWatch. 404 приходит числом — приводим к статусу.
async function requestReportStatus(filterData) {
    const response = await fetch(url_reverse("api:finstatements:report:status", {}, filterData));
    const data = await response.json();
    if (data?.report_status === 404) {
        data.report_status = "not_exist";
    }
    return data;
}

export function DMRScreen() {
    const navigate = useNavigate();
    const { api_post } = useRequest();
    const [apiData, setApiData] = useState();
    const [filterData, setFilterData] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [showReportInfo, setShowReportInfo] = useState(false);
    // Состояние для подтверждения удаления отчета
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Состояние для подтверждения разблокировки отчета
    const [showReportUnlockConfirm, setShowReportUnlockConfirm] = useState(false);

    // Рефетч после действия — без спиннера: он подменяет собой всё тело карточки.
    const refreshReportStatus = async () => {
        try {
            setApiData(await requestReportStatus(filterData));
        } catch (error) {
            console.error("Ошибка загрузки данных:", error);
        }
    };

    useEffect(() => {
        const getParams = parseQS();
        if (!getParams.payment_period) {
            getParams.payment_period = prevMonthPeriod();
        }
        if (!getParams.project) {
            getParams.project = "store";
        }
        setFilterData(getParams);
    }, []);

    // Статус отчёта меняется раз в минуты и живёт в MoneyWatch, поэтому опрашиваем его коротким
    // запросом, пока вкладка видима. Первый запрос после смены фильтра — со спиннером, остальные
    // тихие. Опрос не останавливается на терминальном статусе: отчёт пересоздают и разблокируют
    // прямо с этой страницы, в том числе из соседней вкладки.
    useEffect(() => {
        if (!filterData) {
            return undefined;
        }
        navigate({ search: "?" + buildQS(filterData) }, { replace: true });

        let timer = null;
        let cancelled = false;
        let delay = POLL_INTERVAL_MS;
        let initial = true;

        const poll = async () => {
            if (!initial && document.visibilityState !== "visible") {
                timer = setTimeout(poll, delay);
                return;
            }
            if (initial) {
                setIsLoading(true);
            }
            try {
                const data = await requestReportStatus(filterData);
                if (cancelled) {
                    return;
                }
                // Недоступный MoneyWatch логирует ошибку в Sentry на каждый запрос — разводим опросы.
                delay =
                    data?.report_status === "fetch_error"
                        ? Math.min(delay * 2, POLL_MAX_INTERVAL_MS)
                        : POLL_INTERVAL_MS;
                setApiData(data);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                console.error("Ошибка загрузки данных:", error);
                delay = Math.min(delay * 2, POLL_MAX_INTERVAL_MS);
            } finally {
                if (initial && !cancelled) {
                    setIsLoading(false);
                    initial = false;
                }
            }
            if (!cancelled) {
                timer = setTimeout(poll, delay);
            }
        };

        poll();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [filterData]);

    if (!apiData) {
        return;
    }

    const doAction = (e) => {
        api_post(e, url_reverse("api:finstatements:report:post_actions"), {
            params: { ...filterData, state: e.target.name },
            then: refreshReportStatus,
        });
    };

    const handleFormChange = (prm, val) => {
        const newFormData = { ...filterData };
        newFormData[prm] = val;
        setFilterData(newFormData);
    };

    const statusToText = (status) => {
        switch (status) {
            case "init":
                return { reportStatusText: "Инициализирован", reportStatusBadgeVariant: "secondary" };
            case "collecting_data":
                return { reportStatusText: "Сбор данных", reportStatusBadgeVariant: "info" };
            case "data_collected":
                return { reportStatusText: "Сбор данных завершен", reportStatusBadgeVariant: "success" };
            case "data_collection_error":
                return { reportStatusText: "Ошибка сбора данных", reportStatusBadgeVariant: "danger" };
            case "analyzing":
                return { reportStatusText: "Анализ данных", reportStatusBadgeVariant: "info" };
            case "analyzed":
                return { reportStatusText: "Анализ данных завершен", reportStatusBadgeVariant: "success" };
            case "analysis_error":
                return { reportStatusText: "Ошибка анализа данных", reportStatusBadgeVariant: "danger" };
            case "generating_report":
                return { reportStatusText: "Создание отчета", reportStatusBadgeVariant: "info" };
            case "completed":
                return { reportStatusText: "Отчет создан", reportStatusBadgeVariant: "success" };
            case "failed":
                return { reportStatusText: "Ошибка создания отчета", reportStatusBadgeVariant: "danger" };
            case "not_exist":
                return { reportStatusText: "Нет данных", reportStatusBadgeVariant: "secondary" };
            case "fetch_error":
                return { reportStatusText: "Ошибка получения данных", reportStatusBadgeVariant: "danger" };
            case "locked":
                return { reportStatusText: "Отчет финализирован", reportStatusBadgeVariant: "info" };
            case "publishing":
                return { reportStatusText: "Публикация отчетов...", reportStatusBadgeVariant: "info" };
            case "published":
                return { reportStatusText: "Отчеты опубликованы", reportStatusBadgeVariant: "success" };
            default:
                return { reportStatusText: "Неизвестный статус", reportStatusBadgeVariant: "danger" };
        }
    };

    const { reportStatusText, reportStatusBadgeVariant } = statusToText(apiData.report_status);

    return (
        <>
            <Card>
                <Card.Header>
                    Финансовый отчет по данным ДМР{" "}
                    <span className="float-right">
                        <Badge className="me-3" variant={reportStatusBadgeVariant}>
                            {reportStatusText}
                        </Badge>
                    </span>
                </Card.Header>
                <Card.Body>
                    {isLoading ? (
                        <Row className="justify-content-center">
                            <Col md="auto">
                                <Spinner animation="border" role="status" />
                                <p className="visually-hidden">Загрузка...</p>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row>
                                <Col md={6}>
                                    <Row className="m-2">
                                        <Col>
                                            <b>Период</b>
                                        </Col>
                                        <Col>
                                            <DevForm.DatePicker
                                                value={filterData.payment_period}
                                                onChange={(val) => handleFormChange("payment_period", val)}
                                                showMonthYearPicker={true}
                                                fmt="LLL yyyy"
                                                api_fmt="YYYYMM"
                                                wrapperClassName="w-100"
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="m-2">
                                        <Col>
                                            <b>Разработчик</b>
                                        </Col>
                                        <Col>
                                            <DevForm.SelectSuggest
                                                placeholder="Введите название компании"
                                                choices={apiData.dev_suggest_choices}
                                                value={filterData.developer_id}
                                                suggestUrl={url_reverse(
                                                    "api:suggests",
                                                    {
                                                        suggest_type: "developer",
                                                    },
                                                    {
                                                        search_for_mdm_id: true,
                                                    }
                                                )}
                                                onChange={(ev) => handleFormChange("developer_id", ev.value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="m-2">
                                        <Col>
                                            <b>Проект</b>
                                        </Col>
                                        <Col>
                                            <Dropdown onSelect={(val) => handleFormChange("project", val)}>
                                                <Dropdown.Toggle
                                                    style={{ width: "100%" }}
                                                    variant="outline-primary"
                                                    id="dropdown-basic"
                                                >
                                                    {PROJECTS.find((p) => p.value === filterData.project)?.label ||
                                                        filterData.project}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu style={{ width: "100%" }}>
                                                    {PROJECTS.map((p) => (
                                                        <Dropdown.Item
                                                            key={p.value}
                                                            eventKey={p.value}
                                                            active={filterData.project === p.value}
                                                        >
                                                            {p.label}
                                                        </Dropdown.Item>
                                                    ))}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </Col>
                                    </Row>
                                    <Row></Row>
                                </Col>
                                <Col md={6}>
                                    <Row>
                                        <Col>
                                            {apiData.report_status === "completed" && (
                                                <p>
                                                    <b>За этот период есть сформированный отчет</b>
                                                </p>
                                            )}
                                            <p>
                                                <i>
                                                    Последнее обновление статуса:{" "}
                                                    <SyncedAt last_ts={apiData.report_updated_at} />
                                                </i>
                                            </p>
                                            {filterData.developer_id && (
                                                <p>
                                                    <b style={{ color: "red" }}>ВНИМАНИЕ! </b>
                                                    Перед запросом отчета для разработчика необходимо собрать{" "}
                                                    <b>общий отчет за месяц</b>.
                                                </p>
                                            )}
                                        </Col>
                                    </Row>
                                    <Row className="m-3" />
                                    <Row></Row>
                                </Col>
                            </Row>

                            <hr />

                            <Row className="justify-content-center align-items-center2">
                                {["init", "not_exist", "completed"].includes(apiData.report_status) &&
                                    filterData.developer_id == null && (
                                        <ButtonCol
                                            name={"data_sync"}
                                            variant={"outline-primary"}
                                            disabled={["collecting_data", "analyzing", "generating_report"].includes(
                                                apiData.report_status
                                            )}
                                            awesomeIcon={"download"}
                                            label={"Загрузить транзакции в БД"}
                                            onClick={doAction}
                                        />
                                    )}
                                {/*отображаем кнопку формирования отчета только если есть данные*/}
                                {((["analyzed", "completed"].includes(apiData.report_status) &&
                                    filterData.developer_id == null) ||
                                    filterData.developer_id != null) && (
                                    <ButtonCol
                                        name={"report_make"}
                                        variant={"outline-primary"}
                                        disabled={false}
                                        awesomeIcon={"calculator"}
                                        label={"Сформировать отчет"}
                                        onClick={doAction}
                                    />
                                )}
                                {/*отображаем кнопку сводной информации об отчете, только тогда если есть отчет*/}
                                {["completed", "locked", "publishing", "published"].includes(apiData.report_status) && (
                                    <>
                                        <ButtonCol
                                            name={"report_info"}
                                            variant={"outline-primary"}
                                            disabled={false}
                                            awesomeIcon={"info-circle"}
                                            label={"Сводная информация"}
                                            onClick={() => setShowReportInfo(true)}
                                        />
                                        <ButtonCol
                                            name={"report_download"}
                                            variant={"outline-success"}
                                            disabled={false}
                                            awesomeIcon={"download"}
                                            label={"Скачать отчет"}
                                            onClick={() => getAttachement(fileDownloadLink(apiData.report_file))}
                                        />
                                    </>
                                )}
                                {/* Логика финализации отчета */}
                                {filterData.developer_id == null && ["completed"].includes(apiData.report_status) && (
                                    <ButtonCol
                                        name={"report_lock"}
                                        variant={"outline-primary"}
                                        disabled={false}
                                        awesomeIcon={"lock"}
                                        label={"Финализировать отчет"}
                                        onClick={doAction}
                                    />
                                )}
                                {["locked"].includes(apiData.report_status) && (
                                    <ButtonCol
                                        name={"report_publish"}
                                        variant={"outline-primary"}
                                        disabled={false}
                                        awesomeIcon={"bullhorn"}
                                        label={"Опубликовать отчеты"}
                                        onClick={doAction}
                                    />
                                )}
                                {![
                                    "collecting_data",
                                    "data_collected",
                                    "data_collection_error",
                                    "analyzing",
                                    "analyzed",
                                    "analysis_error",
                                    "generating_report",
                                    "failed",
                                    "completed",
                                ].includes(apiData.report_status) && <Col md={3} xs="auto" />}
                                {filterData.developer_id == null &&
                                    [
                                        "collecting_data",
                                        "data_collected",
                                        "data_collection_error",
                                        "analyzing",
                                        "analyzed",
                                        "analysis_error",
                                        "generating_report",
                                        "completed",
                                        "failed",
                                    ].includes(apiData.report_status) && (
                                        <ButtonCol
                                            name={"data_cleanup"}
                                            variant={"outline-danger"}
                                            disabled={false}
                                            awesomeIcon={"trash"}
                                            label={"Удалить данные"}
                                            onClick={() => setShowDeleteConfirm(true)}
                                        />
                                    )}
                                {filterData.developer_id == null && ["locked"].includes(apiData.report_status) && (
                                    <ButtonCol
                                        name={"report_unlock"}
                                        variant={"outline-primary"}
                                        disabled={false}
                                        awesomeIcon={"unlock"}
                                        label={"Разблокировать отчет"}
                                        onClick={() => setShowReportUnlockConfirm(true)}
                                    />
                                )}
                            </Row>
                        </>
                    )}
                </Card.Body>
            </Card>

            <UploadFilesBlock
                uploadFileTypes={[
                    { value: "tbank_report", label: "Отчет Т-Банк" },
                    { value: "sber_report", label: "Отчет Cбер" },
                ]}
            />

            <ReportInfoModal show={showReportInfo} setShow={setShowReportInfo} />
            <ReportDeleteConfirmModal
                show={showDeleteConfirm}
                setShow={setShowDeleteConfirm}
                onConfirm={() => {
                    api_post(null, url_reverse("api:finstatements:report:post_actions"), {
                        params: { ...filterData, state: "data_cleanup" },
                        then: refreshReportStatus,
                    });
                }}
            />
            <ReportUnlockConfirmModal
                show={showReportUnlockConfirm}
                setShow={setShowReportUnlockConfirm}
                onConfirm={() => {
                    api_post(null, url_reverse("api:finstatements:report:post_actions"), {
                        params: { ...filterData, state: "report_unlock" },
                        then: refreshReportStatus,
                    });
                }}
            />
        </>
    );
}

export function PaymentsControlScreen() {
    const { t } = useTranslation();
    const [apiData, setApiData] = useState();
    const [paymentPeriod, setPaymentPeriod] = useState();
    const { api_get } = useRequest();
    const navigate = useNavigate();
    const location = useLocation();

    const changePeriod = (val) => {
        navigate({ search: "?" + buildQS({ payment_period: val }) }, { replace: true });
    };

    useEffect(() => {
        const getParams = parseQS();
        if (getParams.payment_period) {
            setPaymentPeriod(getParams.payment_period);
        } else {
            changePeriod(prevMonthPeriod());
        }
    }, [location]);

    useEffect(() => {
        if (paymentPeriod) {
            const apiUrl = url_reverse(
                "api:finstatements:report:info",
                {},
                {
                    payment_period: paymentPeriod,
                    project: "store",
                }
            );

            api_get(null, apiUrl, {
                then: (resp) => {
                    setApiData(resp);
                },
            });
        }
    }, [paymentPeriod]);

    if (!apiData) return;

    const fileReport = Object.fromEntries(apiData.files.map((file) => [file.file_type, file.file_url]));

    return (
        <>
            <Card>
                <Card.Header>{t("Контроль оплат")}</Card.Header>
                <Card.Body>
                    <FormFieldGroup label={t("Период")}>
                        <DevForm.DatePicker
                            value={paymentPeriod}
                            onChange={(val) => changePeriod(val)}
                            showMonthYearPicker={true}
                            fmt="LLL yyyy"
                            api_fmt="YYYYMM"
                            wrapperClassName="w-100"
                        />
                    </FormFieldGroup>
                    <Row className="justify-content-center align-items-center2">
                        <ButtonCol
                            name={"report_download"}
                            variant={"outline-success"}
                            disabled={!fileReport?.payment_control}
                            awesomeIcon={"download"}
                            label={"Скачать отчет"}
                            onClick={() => getAttachement(fileDownloadLink(fileReport?.payment_control))}
                        />
                    </Row>
                </Card.Body>
            </Card>

            <UploadFilesBlock
                uploadFileTypes={[
                    { value: "onec_royalty_xlsx", label: "Выгрузка 1С по выплатам разработчикам" },
                    { value: "payment_registry", label: "Реестр выплат" },
                ]}
                paymentPeriod={paymentPeriod}
            />
        </>
    );
}
